import React, { useEffect, useRef, useState } from 'react';

const stats = [
  { num: '500+', label: 'Happy Customers' },
  { num: '24/7', label: 'Always Available' },
  { num: '30min', label: 'Avg Response Time' },
  { num: '100%', label: 'Satisfaction Rate' },
];

const features = [
  { icon: '🚗', title: 'We Come to You', desc: 'No need to visit a shop. We arrive at your home, workplace or roadside.' },
  { icon: '⚡', title: 'Fast Response', desc: 'Most customers are helped within 30–45 minutes of calling us.' },
  { icon: '💰', title: 'Honest Pricing', desc: 'No hidden fees. Transparent, fair pricing every single time.' },
  { icon: '🔒', title: 'Safety First', desc: 'All tires are safety-inspected before installation on your vehicle.' },
];

const AboutUs = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div style={styles.section} id="about" ref={ref}>
      {/* Background image with overlay */}
      <div style={styles.bgImage} />
      <div style={styles.overlay} />

      <div style={styles.container}>
        {/* Header */}
        <div style={{
          ...styles.header,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease'
        }}>
          <p style={styles.tag}>ABOUT US</p>
          <h2 style={styles.title}>Empire1Tires, NYC</h2>
          <p style={styles.subtitle}>
            We are New York's premier mobile tire service. Born from a simple idea,
            that getting a tire changed shouldn't mean waiting hours at a shop.
            We bring professional tire service directly to you, wherever you are.
          </p>
        </div>

        {/* Main Content Grid */}
        <div style={styles.mainGrid} className="about-grid">
          {/* Left — Image Stack */}
          <div style={{
            ...styles.imageStack,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(-40px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s'
          }} className="about-image-stack" >
            <div style={styles.imagePrimary}>
              <img
                src="https://images.unsplash.com/photo-1625047509168-a7026f36de04?w=600&q=80"
                alt="Tire service"
                style={styles.img}
              />
            </div>
            <div style={styles.imageSecondary}>
              <img
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80"
                alt="Mobile tire repair"
                style={styles.img}
              />
            </div>
            <div style={styles.imageBadge}>
              <p style={styles.badgeNum}>10+</p>
              <p style={styles.badgeText}>Years Experience</p>
            </div>
          </div>

          {/* Right — Content */}
          <div style={{
            ...styles.content,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(40px)',
            transition: 'opacity 0.8s ease 0.3s, transform 0.8s ease 0.3s'
          }}>
            <h3 style={styles.contentTitle}>
              Fast, Reliable & Affordable<br />
              <span style={styles.contentHighlight}>Mobile Tire Service</span>
            </h3>
            <p style={styles.contentText}>
              Stuck with a flat tire in Clark? Whether you're commuting along
              Westfield Avenue, Rahway River Parkway, or the Garden State Parkway,
              Empire1Tires comes directly to your location.
            </p>
            <p style={styles.contentText}>
              We provide fast, affordable mobile tire changes and used tire
              replacements, so you can safely get back on the road.
              Help is already on the way.
            </p>

            {/* Features Grid */}
            <div style={styles.featuresGrid} className="features-grid" >
              {features.map((f, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.featureCard,
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                    transition: `opacity 0.6s ease ${0.4 + i * 0.1}s, transform 0.6s ease ${0.4 + i * 0.1}s`
                  }}
                >
                  <span style={styles.featureIcon}>{f.icon}</span>
                  <div>
                    <p style={styles.featureTitle}>{f.title}</p>
                    <p style={styles.featureDesc}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{
          ...styles.statsRow,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease 0.6s, transform 0.8s ease 0.6s'
        }} className="stats-row" >
          {stats.map((stat, i) => (
            <div key={i} style={styles.statItem}>
              <h3 style={styles.statNum}>{stat.num}</h3>
              <p style={styles.statLabel}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  section: {
    position: 'relative',
    padding: '100px 20px',
    overflow: 'hidden'
  },
  bgImage: {
    position: 'absolute',
    inset: 0,
    backgroundImage: 'url(https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=1600&q=80)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: 'brightness(0.2)',
    zIndex: 0
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(26,58,92,0.92) 0%, rgba(26,58,92,0.85) 100%)',
    zIndex: 1
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    position: 'relative',
    zIndex: 2
  },
  header: {
    textAlign: 'center',
    marginBottom: '60px'
  },
  tag: {
    color: '#c41e3a',
    fontSize: '12px',
    fontWeight: 'bold',
    letterSpacing: '3px',
    marginBottom: '12px'
  },
  title: {
    color: '#fff',
    fontSize: '42px',
    fontWeight: '900',
    marginBottom: '16px'
  },
  subtitle: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '17px',
    lineHeight: 1.8,
    maxWidth: '680px',
    margin: '0 auto'
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    alignItems: 'center',
    marginBottom: '60px'
  },
  imageStack: {
    position: 'relative',
    height: '420px'
  },
  imagePrimary: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '75%',
    height: '85%',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    border: '3px solid rgba(255,255,255,0.1)'
  },
  imageSecondary: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '55%',
    height: '55%',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    border: '3px solid #c41e3a'
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  imageBadge: {
    position: 'absolute',
    top: '50%',
    left: '60%',
    transform: 'translate(-50%, -50%)',
    background: '#c41e3a',
    borderRadius: '50%',
    width: '90px',
    height: '90px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(196,30,58,0.5)',
    zIndex: 3
  },
  badgeNum: { color: '#fff', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  badgeText: { color: 'rgba(255,255,255,0.85)', fontSize: '10px', margin: 0, textAlign: 'center' },
  content: {},
  contentTitle: {
    color: '#fff',
    fontSize: '32px',
    fontWeight: 'bold',
    lineHeight: 1.3,
    marginBottom: '20px'
  },
  contentHighlight: { color: '#c41e3a' },
  contentText: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '15px',
    lineHeight: 1.8,
    marginBottom: '16px'
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginTop: '28px'
  },
  featureCard: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    background: 'rgba(255,255,255,0.06)',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid rgba(255,255,255,0.1)'
  },
  featureIcon: { fontSize: '24px', flexShrink: 0 },
  featureTitle: { color: '#fff', fontSize: '14px', fontWeight: 'bold', margin: '0 0 4px' },
  featureDesc: { color: 'rgba(255,255,255,0.65)', fontSize: '12px', lineHeight: 1.6, margin: 0 },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px',
    background: 'rgba(255,255,255,0.06)',
    borderRadius: '16px',
    padding: '32px',
    border: '1px solid rgba(255,255,255,0.1)',
    backdropFilter: 'blur(10px)'
  },
  statItem: { textAlign: 'center' },
  statNum: { color: '#c41e3a', fontSize: '36px', fontWeight: '900', margin: '0 0 4px' },
  statLabel: { color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }
};

export default AboutUs;